/**
 * Wire → ViewModel for the per-row result shape.
 *
 * Mostly a rename from snake_case to camelCase, which is the point: the
 * backend now decides what a row MEANS (which candidate it points at, whether
 * a target exists at all, what its score is evidence of), so the client no
 * longer digs through an open metadata bag to work it out. The three places
 * this does more than rename are called out at their site:
 *
 *  - `failed` is folded back onto `reviewStatus` for the UI;
 *  - an OE target is marked as a PROPOSAL rather than a catalogue record;
 *  - the alternatives list is built against the kind its row already declared,
 *    since that response carries no discriminator of its own.
 */

import { MappingStatus, ReviewStatus } from "@models/job";
import { MappingResultKind } from "@models/mappingResult";
import { buildJobErrorViewModel } from "@utils/builders/job";
import type {
  MappingResult,
  MappingResultResponseWire,
  MappingResultRow,
  MappingResultRowOeWire,
  MappingResultRowTmWire,
  MappingResultRowWire,
  OeSource,
  OeSourceWire,
  OeTarget,
  OeTargetWire,
  RowAlternatives,
  RowAlternativesResponseWire,
  TmSource,
  TmSourceWire,
  TmTarget,
  TmTargetWire,
} from "@models/mappingResult";

/**
 * `failed` is display-only on the client.
 *
 * The backend keeps it out of `review_status` — a row the pipeline could not
 * map was never reviewed, so it stays `pending` there — and reports it as a
 * `mapping_status`. Folding it back onto one field keeps the Status column,
 * its filter chip and the disabled row actions reading a single value. It is
 * never sent back: the PATCH request type has no `failed`.
 */
const displayReviewStatus = (row: MappingResultRowWire): ReviewStatus =>
  row.mapping_status === MappingStatus.Failed
    ? ReviewStatus.Failed
    : row.review_status;

export const buildTmSource = (source: TmSourceWire): TmSource => ({
  cleanedText: source.cleaned_text,
  path: source.path,
  rawText: source.raw_text,
  variant: source.variant,
  category: source.category,
  remark: source.remark,
});

export const buildTmTarget = (target: TmTargetWire): TmTarget => ({
  rank: target.rank,
  key: target.key,
  valueDisplay: target.value_display,
  itemMpId: target.item_mp_id,
  itemMpListing: target.item_mp_listing,
  unit: target.unit,
  score: target.score,
  scoreBasis: target.score_basis,
  exactValueMatch: target.exact_value_match,
});

export const buildOeSource = (source: OeSourceWire): OeSource => ({
  oeNumber: source.oe_number,
  oeName: source.oe_name,
  oeFunction: source.oe_function,
  oeDomain: source.oe_domain,
});

export const buildOeTarget = (target: OeTargetWire): OeTarget => ({
  rank: target.rank,
  itemMpId: target.itemmp_id,
  itemMpText: target.itemmp_text,
  scores: target.scores ?? {},
  mainGroup: target.main_group,
  subGroup: target.sub_group,
  score: target.score,
  suggestedText: target.suggested_text,
  reason: target.reason,
  confidenceBand: target.confidence_band,
  // Derived rather than sent, because it is not a separate fact: a proposal is
  // exactly a target that names something the catalogue does not have. The
  // backend leaves BOTH catalogue fields empty on one, so "has a suggested
  // name and no id" is the whole definition.
  isProposal: !!target.suggested_text && target.itemmp_id === null,
});

const buildTmRow = (row: MappingResultRowTmWire): MappingResultRow => ({
  kind: MappingResultKind.Tm,
  rowIndex: row.row_index,
  reviewStatus: displayReviewStatus(row),
  mappingStatus: row.mapping_status,
  hasExamples: row.has_examples,
  hasAlternative: row.has_alternative,
  error: buildJobErrorViewModel(row.error),
  chosenRank: row.chosen_rank,
  source: buildTmSource(row.source),
  target: row.target ? buildTmTarget(row.target) : null,
});

const buildOeRow = (row: MappingResultRowOeWire): MappingResultRow => ({
  kind: MappingResultKind.Oe,
  rowIndex: row.row_index,
  reviewStatus: displayReviewStatus(row),
  mappingStatus: row.mapping_status,
  hasExamples: row.has_examples,
  hasAlternative: row.has_alternative,
  error: buildJobErrorViewModel(row.error),
  chosenRank: row.chosen_rank,
  source: buildOeSource(row.source),
  target: row.target ? buildOeTarget(row.target) : null,
});

export const buildMappingResultRow = (
  row: MappingResultRowWire,
): MappingResultRow =>
  row.kind === MappingResultKind.Oe ? buildOeRow(row) : buildTmRow(row);

export const buildMappingResult = (
  response: MappingResultResponseWire,
): MappingResult => ({
  jobId: response.job_id,
  status: response.status,
  pluginId: response.plugin_id,
  rows: (response.rows ?? []).map(buildMappingResultRow),
  cursor: response.cursor,
  display: response.display,
});

/**
 * The candidates for one row.
 *
 * `kind` is a parameter, not something read off the payload: that response
 * carries no discriminator, deliberately — one request is one row, and the
 * caller opened the dropdown ON that row, so it already knows. Passing it in
 * keeps the decision where the knowledge is instead of sniffing field names.
 *
 * Rank 1 is in the list and belongs there: after a pick the row's `target` is
 * the chosen candidate, so this is the only place the pipeline's own top match
 * is still reachable — picking it back is how a correction is undone. Render
 * the list in the order given; `target.rank` is what says which entry is the
 * one currently in effect.
 */
export const buildRowAlternatives = (
  response: RowAlternativesResponseWire,
  kind: MappingResultKind,
): RowAlternatives => {
  const alternatives = response.alternatives ?? [];
  if (kind === MappingResultKind.Oe) {
    return {
      kind,
      jobId: response.job_id,
      rowIndex: response.row_index,
      alternatives: (alternatives as OeTargetWire[]).map(buildOeTarget),
    };
  }
  return {
    kind,
    jobId: response.job_id,
    rowIndex: response.row_index,
    alternatives: (alternatives as TmTargetWire[]).map(buildTmTarget),
  };
};
